import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { MapPin, Users, Calendar, Star, Home, Car, Flame, Signal, Mountain, Volume2, Sparkles, PawPrint, Leaf, Footprints, Phone, Mail, User as UserIcon } from 'lucide-react';
import MarkdownText from './MarkdownText';

/**
 * Read-only preview of a location's full details. Used by admins to
 * review a submission before approval – shows every field the host
 * filled in, without allowing edits.
 */
export default function LocationPreviewDialog({ open, onOpenChange, location }) {
  if (!location) return null;

  const price = [];
  if (location.pricing_mode === 'per_unit') {
    if (location.price_per_adult > 0) price.push(['Dospělý', `${location.price_per_adult} Kč/noc`]);
    if (location.price_per_child > 0) price.push(['Dítě', `${location.price_per_child} Kč/noc`]);
    if (location.price_per_car > 0) price.push(['Auto', `${location.price_per_car} Kč/noc`]);
    if (location.price_per_tent > 0) price.push(['Stan', `${location.price_per_tent} Kč/noc`]);
    if (location.price_per_cabin_unit > 0) price.push(['Chata (objekt)', `${location.price_per_cabin_unit} Kč/noc`]);
    if (location.price_per_cabin > 0) price.push(['Chata (osoba)', `${location.price_per_cabin} Kč/noc/osoba`]);
  } else {
    price.push(['Jednotná cena', `${location.price} Kč/noc`]);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="location-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <span>{location.name}</span>
            <Badge className={location.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
              {location.status || 'pending'}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Photos */}
          {location.photos?.length > 0 && (
            <div>
              <SectionTitle>Fotky ({location.photos.length})</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {location.photos.slice(0, 9).map((p, i) => (
                  <img key={i} src={p} alt={`Foto ${i + 1}`} className="w-full h-32 object-cover rounded-lg" loading="lazy" />
                ))}
              </div>
            </div>
          )}

          {/* Basic info */}
          <div>
            <SectionTitle>Základní údaje</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Field icon={<MapPin className="w-4 h-4" />} label="Kategorie" value={location.category} />
              <Field icon={<MapPin className="w-4 h-4" />} label="Typ" value={location.type} />
              <Field icon={<Users className="w-4 h-4" />} label="Max. kapacita" value={location.max_capacity ? `${location.max_capacity} osob` : '—'} />
              <Field icon={<Car className="w-4 h-4" />} label="Přístup" value={location.accessibility} />
              <Field icon={<Car className="w-4 h-4" />} label="Parkování" value={
                location.parking_type === 'on_site' ? 'Na pozemku'
                : location.parking_type === 'off_site' ? `Mimo pozemek (${location.parking_distance_m || 0} m)`
                : location.parking_type === 'none' ? 'Bez parkování'
                : '—'
              } />
              <Field icon={<Mountain className="w-4 h-4" />} label="Terén" value={location.terrain} />
              <Field icon={<MapPin className="w-4 h-4" />} label="GPS" value={location.latitude && location.longitude ? `${location.latitude}, ${location.longitude}` : '—'} />
              <Field icon={<Calendar className="w-4 h-4" />} label="Příjezd od" value={location.check_in_time || '—'} />
              <Field icon={<Calendar className="w-4 h-4" />} label="Odjezd do" value={location.check_out_time || '—'} />
            </div>
          </div>

          {/* Pricing */}
          <div>
            <SectionTitle>Ceník ({location.pricing_mode === 'per_unit' ? 'detailní' : 'jednotný'})</SectionTitle>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              {price.length === 0 ? (
                <p className="text-red-600 text-sm">⚠ Ceník není vyplněn</p>
              ) : (
                <ul className="text-sm space-y-1">
                  {price.map(([lbl, val]) => (
                    <li key={lbl} className="flex justify-between">
                      <span className="text-gray-700">{lbl}</span>
                      <span className="font-semibold">{val}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Descriptions */}
          <div>
            <SectionTitle>Krátký popis</SectionTitle>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{location.description || <span className="text-red-600">⚠ Chybí</span>}</p>
          </div>
          <div>
            <SectionTitle>Podrobný popis</SectionTitle>
            {location.full_description ? (
              <div className="text-sm text-gray-700 whitespace-pre-wrap"><MarkdownText text={location.full_description} /></div>
            ) : (
              <p className="text-red-600 text-sm">⚠ Chybí</p>
            )}
          </div>

          {/* Environment ratings */}
          <div>
            <SectionTitle>Prostředí</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Rating icon={<Sparkles className="w-4 h-4" />} label="Pozorování hvězd" value={location.stargazing_rating} />
              <Rating icon={<PawPrint className="w-4 h-4" />} label="Pozorování zvěře" value={location.wildlife_rating} />
              <Rating icon={<Leaf className="w-4 h-4" />} label="Míra samoty" value={location.privacy_rating} />
              <Rating icon={<Mountain className="w-4 h-4" />} label="Výhled do přírody" value={location.view_rating} />
              <Rating icon={<Volume2 className="w-4 h-4" />} label="Zvuky přírody" value={location.sounds_rating} />
              <Field icon={<Signal className="w-4 h-4" />} label="Mobilní signál" value={location.mobile_signal} />
              <Field icon={<Footprints className="w-4 h-4" />} label="Turistický pohyb" value={location.tourist_traffic} />
            </div>
          </div>

          {/* Rules */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <SectionTitle>Základní pravidla</SectionTitle>
              <ul className="space-y-1">
                <Rule ok={location.rules?.tents_allowed} label="Stany" />
                <Rule ok={location.rules?.caravans_allowed} label="Karavany / obytné vozy" />
                <Rule ok={location.rules?.pets_allowed} label="Domácí mazlíčci" />
                <Rule ok={location.rules?.smoking_allowed} label="Kouření" />
                <Rule ok={location.rules?.fire_allowed} label="Rozdělávání ohně" />
              </ul>
            </div>
            <div>
              <SectionTitle>Vybavení</SectionTitle>
              {location.facilities?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {location.facilities.map((f) => <Badge key={f} variant="outline">{f}</Badge>)}
                </div>
              ) : (
                <p className="text-gray-500 text-xs">—</p>
              )}
            </div>
          </div>

          {location.owner_rules && (
            <div>
              <SectionTitle>Pravidla majitele</SectionTitle>
              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded whitespace-pre-wrap"><MarkdownText text={location.owner_rules} /></div>
            </div>
          )}
          {location.arrival_instructions && (
            <div>
              <SectionTitle>Pokyny pro příjezd</SectionTitle>
              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded whitespace-pre-wrap"><MarkdownText text={location.arrival_instructions} /></div>
              {(location.arrival_images?.length > 0 || location.arrival_image) && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {(location.arrival_images?.length > 0 ? location.arrival_images : [location.arrival_image]).filter(Boolean).map((img, i) => (
                    <img key={i} src={img} alt={`Pokyny ${i + 1}`} className="w-full h-24 object-cover rounded" loading="lazy" />
                  ))}
                </div>
              )}
            </div>
          )}
          {location.open_access === false && location.open_access_instructions && (
            <div>
              <SectionTitle>Vstup na pozemek (zámky/branky)</SectionTitle>
              <div className="text-sm text-gray-700 bg-yellow-50 border border-yellow-200 p-3 rounded"><MarkdownText text={location.open_access_instructions} /></div>
            </div>
          )}

          {/* Owner */}
          <div>
            <SectionTitle>Majitel</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm bg-blue-50 border border-blue-200 rounded p-3">
              <Field icon={<UserIcon className="w-4 h-4" />} label="Jméno" value={location.owner?.name} />
              <Field icon={<Mail className="w-4 h-4" />} label="E-mail" value={location.owner?.email} />
              <Field icon={<Phone className="w-4 h-4" />} label="Telefon" value={location.owner?.phone} />
            </div>
          </div>

          {/* Booking config */}
          <div>
            <SectionTitle>Nastavení rezervací</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <Field label="Okamžitá rezervace" value={location.instant_book ? 'Ano' : 'Ne (schvaluje majitel)'} />
              <Field label="Host ready (Stripe)" value={location.host_ready ? '✅ Ano' : '⏳ Ne'} />
              <Field label="Aktivní" value={location.is_active !== false ? 'Ano' : 'Pozastaveno'} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="font-bold text-sm text-gray-800 uppercase tracking-wide mb-2 border-b border-gray-200 pb-1">{children}</h3>
);

const Field = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    {icon && <span className="text-gray-400 mt-0.5">{icon}</span>}
    <div>
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="font-medium text-gray-800">{value || '—'}</p>
    </div>
  </div>
);

const Rating = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    {icon && <span className="text-orange-500 mt-0.5">{icon}</span>}
    <div>
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="font-semibold text-gray-800 flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={`w-3 h-3 ${i < (value || 0) ? 'text-orange-500 fill-orange-500' : 'text-gray-300'}`} />
        ))}
        <span className="text-xs">{value || 0}/5</span>
      </p>
    </div>
  </div>
);

const Rule = ({ ok, label }) => (
  <li className="flex items-center gap-2">
    {ok ? <span className="text-green-600">✓</span> : <span className="text-red-500">✕</span>}
    <span className={ok ? 'text-gray-800' : 'text-gray-500'}>{label}</span>
  </li>
);
